import React, { useCallback, useContext, useRef, useState } from 'react';
import { useSelector } from '../../redux';
import {
  Container,
  ImageButton,
  MobileMenuButton,
  HeaderBarWrapper,
  NavbarIcon,
  HeaderFlexContainer,
  LogoLink,
  MenuNavAnchor,
  LogoImage,
} from './styles';
import cx from 'classnames';
import { UpgradeStrip } from './UpgradeStrip';
import HeaderStrip from '@tovia/man-ui/lib/components/HeaderStrip/HeaderStrip';
import styled from 'styled-components/macro';
import { PopoverSitePicker } from './PopoverSitePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { MobileMenuDrawer } from './MobileMenu/MobileMenuDrawer';
import { useHistory } from 'react-router-dom';
import Tooltip from '@tovia/man-ui/lib/components/Tooltip/Tooltip';
import { UserMenuItems } from './UserMenuItems';
import { InternalLinks } from './InternalLinks';
import SearchBoxContainer from '../SearchBox/SearchBox';
import { RecentsBox } from './RecentsBox/RecentsBox';
import SiteNotificationsPanel from '../SiteNotifications/SiteNotifications';
import { SubmissionError } from 'redux-form';
import { HeaderContext } from './HeaderProvider';
import { ConstrainedContainer, TABLET_LANDSCAPE } from '../../constants/mediaQueries';
import { JoinButton, useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { PromoHeaderRibbon } from 'src/client/containers/Promo/PromoHeaderRibbon';
import { usePromotions } from 'src/client/helpers/usePromotions';
import { PartnerHeaderBanner } from 'src/client/components/PartnerBanners/PartnerHeaderBanner';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { logoUrl } from 'src/client/helpers/urls/logoUrl';
import { useWidePageContext } from '../App/WidePageContextProvider';
import { useStaticSplashPage } from 'src/client/helpers/useFeatureFlags';
import { PillButton } from 'src/client/pages/StaticSplash/styles';

export const HeaderBar = () => {
  const user = useSelector((state) => state.auth.user);
  const site = useSelector((state) => state.site);
  const config = useSelector((state) => state.app.config);
  const cdnUrl = useSelector((state) => state.app.cdnUrl);
  const history = useHistory();

  const mainHeader = useRef<HTMLDivElement>(null);
  const searchButtonDisabledRef = useRef(false);

  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { isPromoRibbonActive } = usePromotions();
  const { mobileMenuOpened, setMobileMenuOpened, setActiveNestedMobileMenu } = useContext(HeaderContext);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpened((opened) => {
      if (opened) {
        setActiveNestedMobileMenu(null);
      }

      return !opened;
    });
  }, [setMobileMenuOpened, setActiveNestedMobileMenu]);

  const handleSearchClose = useCallback(() => {
    searchButtonDisabledRef.current = true;
    setSearchBarOpen(false);
    setTimeout(() => {
      searchButtonDisabledRef.current = false;
    }, 100);
  }, []);

  const handleSearchSubmit = useCallback(
    (values) => {
      if (!values.searchTerm || values.searchTerm.trim() === '') {
        throw new SubmissionError({ searchTerm: 'empty value' });
      }
      history.push(`/search/${values.searchTerm || '-'}`);
      setSearchBarOpen(false);
    },
    [history],
  );

  const showFreeMemberJoinButton = user && !user.validSubscription;

  return (
    <>
      <HeaderBarWrapper ref={mainHeader}>
        {isPromoRibbonActive && <PromoHeaderRibbon />}

        <PartnerHeaderBanner />

        <UpgradeStrip />

        {config.headerBar?.showHeaderStrip && <HeaderStrip path={`${cdnUrl}/headerStrips/${site.abbreviation}.png`} />}

        <HeaderContainerSwitcher>
          <HeaderFlexContainer>
            <MobileMenuButton className={cx({ active: mobileMenuOpened })} onClick={toggleMobileMenu}>
              <FontAwesomeIcon icon={mobileMenuOpened ? faArrowLeft : faBars} />
            </MobileMenuButton>

            <MobileMenuDrawer />

            <PopoverSitePicker />

            <LogoLink to="/">
              <LogoImage src={logoUrl(cdnUrl, site)} alt={site.name} />
            </LogoLink>

            <InternalLinks />

            {!user && (
              <>
                <JoinButtonSwitcher campaign="header-bar">Get Started</JoinButtonSwitcher>
                <LoginButton href={`/login?r=${location.pathname.replace(/^\//, '')}`}>Sign In</LoginButton>
              </>
            )}

            {showFreeMemberJoinButton && (
              <StyledJoinButton className="btn btn-primary join-btn" data-value="regular" campaign="header-bar">
                JOIN NOW
              </StyledJoinButton>
            )}

            <Tooltip value="Search" placement="bottom">
              <SearchButton
                className={cx('clickable', { active: searchBarOpen })}
                onClick={() => {
                  if (!searchButtonDisabledRef.current) {
                    setSearchBarOpen((open) => !open);
                  }
                }}
              >
                <NavbarIcon icon={faSearch} />
              </SearchButton>
            </Tooltip>

            {!!user && (
              <UserMenuItems
                key="user-header-menu"
                mainHeader={mainHeader}
                toggleRecentsBox={() => setRecentsOpen((open) => !open)}
                closeNotificationPanel={() => setNotificationsOpen((open) => !open)}
              />
            )}
          </HeaderFlexContainer>
        </HeaderContainerSwitcher>
      </HeaderBarWrapper>

      <SearchBoxContainer isOpened={searchBarOpen} onClose={handleSearchClose} onSubmit={handleSearchSubmit} />

      <RecentsBox isOpened={recentsOpen} onClose={() => setRecentsOpen(false)} />

      {user && config.hasSiteNotifications && (
        <SiteNotificationsPanel active={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
      )}

      <MobileMenuScrim show={mobileMenuOpened} onClick={() => setMobileMenuOpened(false)} />
    </>
  );
};

// @TODO
// Remove after all pages are converted to wide mode.
const HeaderContainerSwitcher: React.FC<{}> = ({ children }) => {
  const widePage = useWidePageContext();
  const { isStaticSplashPage } = useStaticSplashPage();

  if (isStaticSplashPage) {
    return <StaticSplashHeaderContainer>{children}</StaticSplashHeaderContainer>;
  }

  if (widePage) {
    return (
      <Container as={HeaderContainer} extended>
        {children}
      </Container>
    );
  }

  return <Container className="container no-pad">{children}</Container>;
};

const JoinButtonSwitcher: React.FC<{ campaign: string }> = (props) => {
  const { campaign, children } = props;
  const [href] = useJoinUrl({ campaign });
  const { isStaticSplashPage } = useStaticSplashPage();

  if (isStaticSplashPage) {
    return (
      <JoinPillButton as="a" href={href} data-value="regular" data-tagmanager-event="join_cta">
        {children}
      </JoinPillButton>
    );
  }

  return (
    <StyledJoinButton
      campaign={campaign}
      className="btn btn-primary join-btn"
      data-value="regular"
      data-tagmanager-event="join_cta"
    >
      {children}
    </StyledJoinButton>
  );
};

const StaticSplashHeaderContainer = styled.div`
  padding: 0 5px;
`;

const HeaderContainer = styled(ConstrainedContainer)`
  margin: 0 auto;
`;

const MobileMenuScrim = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: #000;
  opacity: 0.6;
  z-index: 1000;
  display: ${(props) => (props.show ? 'block' : 'none')};

  @media ${TABLET_LANDSCAPE} {
    display: none;
  }
`;

const StyledJoinButton = styled(JoinButton)`
  && {
    border-radius: 2px;
    padding: 8px 11px;
    text-transform: uppercase;
    background: ${(props) => props.theme.headerBarPrimary3};
    color: ${(props) => props.theme.primaryBtn1Text};

    @media ${TABLET_LANDSCAPE} {
      padding: 10px 20px;
    }

    &:hover {
      background: ${(props) => props.theme.headerBarPrimary3};
      color: ${(props) => props.theme.primaryBtn1Text};
      opacity: 0.9;
    }
  }
`;

const LoginButton = styled(MenuNavAnchor)`
  margin-left: 5px;
`;

const SearchButton = styled(ImageButton)`
  display: none;
  cursor: pointer;

  @media ${TABLET_LANDSCAPE} {
    display: block;
  }
`;

const JoinPillButton = styled(PillButton)`
  && {
    background-color: var(--brand-color);
    color: #000 !important;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;
